import { useQuery, useQueryClient } from '@tanstack/react-query';
import { bookPlan } from '@kitup/book-schema';
import { useState, type JSX } from 'react';
import { Link } from 'react-router';
import type { BookDto } from '../api/books.js';
import { locationsApi, LOCATION_KINDS, type LocationKind } from '../api/library.js';
import { SelectField } from '../components/Fields.js';
import { EmptyState } from '../components/EmptyState.js';
import { useI18n } from '../i18n/context.js';
import { LibraryPicker } from '../library/LibraryPicker.js';
import { ActionError } from '../library/ActionError.js';
import { useActionFeedback } from '../library/useActionFeedback.js';
import { SimpleEditor } from '../library/SimpleEditor.js';
import { useCreatedItemScroll } from '../library/useCreatedItemScroll.js';

/**
 * Шаг «Локации» (E7-T4) — **опциональный**. Книга без единой локации проходит
 * весь путь до экспорта: тогда места придумывает генератор сюжета (§3.7).
 * Утверждения у локации нет — рендер уходит в сцены сразу после генерации.
 */
export function LocationsStep({
  book,
  readOnly = false,
}: {
  book: BookDto;
  readOnly?: boolean;
}): JSX.Element {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const action = useActionFeedback();

  const linked = useQuery({
    queryKey: ['book-locations', book.id],
    queryFn: () => locationsApi.forBook(book.id),
  });
  const library = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.list(),
    enabled: !readOnly,
  });
  const items = linked.data?.items ?? [];
  const limit = bookPlan(book.plan).maxLocations;
  const { scrollToCreatedItem, itemRef } = useCreatedItemScroll<HTMLLIElement>(
    items.map((item) => item.id),
  );

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['book-locations', book.id] });
    void queryClient.invalidateQueries({ queryKey: ['locations'] });
    void queryClient.invalidateQueries({ queryKey: ['book', book.id] });
  };
  const link = (ids: readonly string[]): Promise<void> =>
    locationsApi
      .linkToBook(
        book.id,
        ids.map((locationId, order) => ({ locationId, order })),
      )
      .then(refresh);

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold">{t.locations.stepTitle}</h1>
        <p className="mt-1 text-sm font-medium text-[var(--kitup-color-muted)]">
          {t.monetization.planLimit(items.length, limit)}
        </p>
        <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.locations.stepSubtitle}</p>
        {/* The empty state below says the same while there is nothing here. */}
        {items.length > 0 ? (
          <p className="mt-1 text-sm text-[var(--kitup-color-muted)]">{t.locations.optional}</p>
        ) : null}
      </div>

      {readOnly || items.length >= limit ? null : (
        <LibraryPicker
          addLabel={t.locations.add}
          createLabel={t.locations.create}
          nameLabel={t.locations.fields.name}
          options={(library.data?.items ?? [])
            .filter((location) => !items.some((item) => item.id === location.id))
            .map((location) => ({
              id: location.id,
              label: location.name,
              detail:
                location.usage.books > 0
                  ? t.library.picker.usedIn(location.usage.books)
                  : t.library.picker.notUsed,
            }))}
          onAdd={(id) => link([...items.map((item) => item.id), id])}
          onCreate={(name) =>
            locationsApi.create({ name, kind: 'room' }).then((created) => {
              setOpenId(created.id);
              scrollToCreatedItem(created.id);
              return link([...items.map((item) => item.id), created.id]);
            })
          }
        />
      )}
      <ActionError message={action.error} />

      {items.length === 0 ? <EmptyState title={t.locations.stepEmpty} /> : null}

      <ul className="grid gap-3">
        {items.map((location) => (
          <li ref={itemRef(location.id)} key={location.id} className="kitup-card p-4">
            <div className="kitup-expandable-row flex flex-wrap items-center justify-between gap-3">
              <button
                type="button"
                className="kitup-expandable-trigger"
                aria-expanded={openId === location.id}
                onClick={() => setOpenId(openId === location.id ? null : location.id)}
              >
                <span>{location.name}</span>
                <span className="kitup-expandable-chevron" aria-hidden="true">
                  ▾
                </span>
              </button>
              <button
                type="button"
                className="kitup-quiet-button"
                disabled={readOnly || action.pending}
                onClick={() =>
                  action.run(
                    link(items.filter((item) => item.id !== location.id).map((item) => item.id)),
                  )
                }
              >
                {t.library.actions.removeFromBook}
              </button>
            </div>
            {openId === location.id ? (
              <div className="mt-4">
                <SimpleEditor
                  entity={location}
                  api={locationsApi}
                  styleId={book.styleId}
                  bookId={book.id}
                  readOnly={readOnly}
                  uploadKind="location_ref"
                  maxPhotos={3}
                  photoHint={t.library.photos.hintLocation}
                  labels={{
                    name: t.locations.fields.name,
                    description: t.locations.fields.description,
                  }}
                  kindField={
                    <SelectField
                      label={t.locations.fields.kind}
                      value={location.kind}
                      options={LOCATION_KINDS.map((kind) => ({
                        value: kind,
                        label: t.locations.kinds[kind],
                      }))}
                      onChange={(kind: LocationKind) =>
                        action.run(locationsApi.update(location.id, { kind }).then(refresh))
                      }
                    />
                  }
                  footer={
                    <p className="mb-3 text-sm text-[var(--kitup-color-muted)]">
                      {t.locations.noApprovalNeeded}
                    </p>
                  }
                  onChanged={refresh}
                />
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      {readOnly ? null : (
        <Link
          to={`/app/locations?bookId=${encodeURIComponent(book.id)}`}
          className="justify-self-start text-sm text-[var(--kitup-color-muted)] underline underline-offset-2"
        >
          {t.library.actions.openLibrary}
        </Link>
      )}
    </div>
  );
}
